import { useCallback, useState } from 'react';

import useDebounce from './useDebounce';

/**
 * Server-paged table page sizes shared by Customers, Invites and Requests
 * (PRD D11: 20 default, 20/50/100 offered).
 */
export const LIST_PAGE_SIZES = [20, 50, 100] as const;
export type ListPageSize = (typeof LIST_PAGE_SIZES)[number];
export const DEFAULT_LIST_PAGE_SIZE: ListPageSize = LIST_PAGE_SIZES[0];

const SEARCH_DEBOUNCE_MS = 400;

export interface UseListStateOptions {
  /**
   * Any value outside search/limit that should also reset the page to 1 when
   * it changes (e.g. a composite of the Division quick filter's scope and the
   * selected Division ids, or the active Requests tab). Pass a stable
   * primitive, or a memoized composite string built from the filter values.
   */
  resetKey?: string | number | boolean;
}

export interface UseListStateReturn {
  page: number;
  setPage: (page: number) => void;
  limit: ListPageSize;
  setLimit: (limit: ListPageSize) => void;
  /** Immediate value bound to the search box. */
  searchInput: string;
  setSearchInput: (value: string) => void;
  /** Debounced, trimmed value to send to the query. */
  search: string;
}

/** What a server-paged list query takes from `useListState` (Customers,
 * Invites, Requests). */
export interface ListQueryParams {
  page: number;
  limit: ListPageSize;
  /** Debounced, trimmed search value from `useListState`. */
  search: string;
  /** Gate for the query; e.g. `useDivisionRollup().isReady` so the first
   * request already has the right `scope`, or whether the tab is active so
   * only its query runs. */
  enabled: boolean;
}

/**
 * Shared list-state hook for the Customers / Invites / Requests tables:
 * page, limit and a debounced search value, with every search, limit or
 * `resetKey` change resetting the page back to 1 (PRD D11).
 *
 * The page is stored together with the `search|limit|resetKey` key it was
 * set under, and reads as 1 whenever that key no longer matches, so the
 * render that first sees a new debounced search, limit or `resetKey` already
 * sees page 1 — no extra request with the old page. Typing does not reset the
 * page until the debounced `search` value changes.
 */
export function useListState({ resetKey }: UseListStateOptions = {}): UseListStateReturn {
  const [limit, setLimit] = useState<ListPageSize>(DEFAULT_LIST_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const currentKey = `${search}|${limit}|${String(resetKey)}`;
  const [pageState, setPageState] = useState({ page: 1, key: currentKey });

  const page = pageState.key === currentKey ? pageState.page : 1;
  const setPage = useCallback(
    (nextPage: number) => setPageState({ page: nextPage, key: currentKey }),
    [currentKey]
  );

  return { page, setPage, limit, setLimit, searchInput, setSearchInput, search };
}
